import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useSearchParams } from 'react-router-dom';
import './App.css';
import { GameDisplay } from './components/GameDisplay';
import { Controller } from './components/Controller';
import { useWebSocket } from './hooks/useWebSocket';
import { useGameState } from './hooks/useGameState';
import { generateUUID } from './utils/uuid';

// Use localhost:3001 in development (direct connection) or window.location.origin in production
const SERVER_URL = process.env.NODE_ENV === 'development' && window.location.port === '3000' 
  ? 'http://localhost:3001' // Direct connection to Express server in development
  : window.location.origin;

// Display component (main game screen)
function DisplayApp() {
  const [gameId, setGameId] = useState<string>('');
  const { socket, connected, reconnecting, pitchData, connectedPlayers, error, gamePaused, pausedWaitingFor, joinGame } = useWebSocket(SERVER_URL);
  
  // Create score update function to send to server
  const sendScoreUpdate = React.useCallback((score: { player1: number; player2: number }) => {
    if (socket && connected && gameId) {
      socket.emit('message', {
        type: 'scoreUpdate',
        gameId,
        score
      });
    }
  }, [socket, connected, gameId]);

  const { gameState: localGameState, updatePitchData, handleCountdown, handleGameStart, handleGameEnd, handleGamePaused, handleGameResumed } = useGameState(gameId, sendScoreUpdate);

  useEffect(() => {
    // Generate unique game ID when component mounts
    const newGameId = generateUUID();
    setGameId(newGameId);
  }, []);

  useEffect(() => {
    // Join game room when connected and gameId is available
    if (connected && gameId && socket) {
      joinGame(gameId);
    }
  }, [connected, gameId, socket, joinGame]);

  // No longer need to update from server game state since all logic is client-side now

  useEffect(() => {
    // Update pitch data in game state hook
    updatePitchData(pitchData);
  }, [pitchData, updatePitchData]);

  // Listen for countdown, game start, game end, and pause/resume messages
  useEffect(() => {
    if (socket) {
      const handleMessage = (message: any) => {
        switch (message.type) {
          case 'countdown':
            handleCountdown(message.seconds);
            break;
          case 'gameStart':
            handleGameStart();
            break;
          case 'gameEnd':
            handleGameEnd(message.winner, message.finalScore);
            break;
          case 'gamePaused':
            handleGamePaused(message.waitingForPlayer);
            break;
          case 'gameResumed':
            handleGameResumed();
            break;
          default:
            break;
        }
      };

      socket.on('message', handleMessage);

      return () => {
        socket.off('message', handleMessage);
      };
    }
  }, [socket, handleCountdown, handleGameStart, handleGameEnd, handleGamePaused, handleGameResumed]);

  // Use local game state with player connections from WebSocket only
  const displayGameState = {
    ...localGameState,
    players: connectedPlayers
  };

  // Remove debug logging - too noisy

  return (
    <div className="App">
      <header className="App-header">
        <h1>SingPong</h1>
        <p>Voice-controlled Pong game</p>
        
        {/* Connection Status */}
        <div style={{ margin: '10px 0' }}>
          <span style={{ 
            color: connected ? '#0f0' : '#f00',
            fontWeight: 'bold'
          }}>
            WebSocket: {connected ? 'Connected' : 'Disconnected'}
          </span>
          {error && (
            <div style={{ color: '#f00', marginTop: '5px' }}>
              Error: {error}
            </div>
          )}
        </div>
      </header>

      <main>
        {gameId && (
          <GameDisplay 
            gameState={displayGameState} 
            gameId={gameId}
            pitchData={pitchData}
            connected={connected}
            reconnecting={reconnecting}
            error={error}
            gamePaused={gamePaused}
            pausedWaitingFor={pausedWaitingFor}
          />
        )}
      </main>
    </div>
  );
}

// Controller component (phone interface)
function ControllerApp() {
  const [searchParams] = useSearchParams();
  const gameId = searchParams.get('gameId');

  // Debug logging (removed for production)

  if (!gameId) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>SingPong Controller</h1>
          <p style={{ color: '#f00' }}>Error: No game ID provided</p>
          <p>Please scan the QR code from the game display to join.</p>
        </header>
      </div>
    );
  }

  return <Controller gameId={gameId} />;
}

// Main App with routing
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<DisplayApp />} />
        <Route path="/controller" element={<ControllerApp />} />
      </Routes>
    </Router>
  );
}

export default App;